import { Card, CardHeader, CardContent, CardTitle } from '@/shared/components/ui/card'

export interface FunnelStep {
  label: string
  value: number
  percentage: number
  color: string
}

// 4 tonos por calificación (300-400-500-600)
export const SCORE_PALETTES: Record<string, [string, string, string, string]> = {
  'A+': ['#047857', '#059669', '#10b981', '#34d399'], // emerald 700-600-500-400
  'A':  ['#0369a1', '#0284c7', '#0ea5e9', '#38bdf8'], // sky 700-600-500-400
  'B':  ['#b45309', '#d97706', '#f59e0b', '#fbbf24'], // amber 700-600-500-400
  'C':  ['#be123c', '#e11d48', '#f43f5e', '#fb7185'], // rose 700-600-500-400
}
export const DEFAULT_PALETTE: [string, string, string, string] = ['#374151', '#4b5563', '#6b7280', '#9ca3af'] // gray 700-600-500-400

interface ConversionFunnelProps {
  base: number
  entrega: number
  aperturas: number
  clics: number
  tasa_entrega: number
  tasa_aperturas: number
  tasa_clics: number
  /** Override default steps (for WhatsApp, etc.) */
  customSteps?: FunnelStep[]
  /** Calificación del score para colorear el embudo (A+, A, B, C) */
  calificacion?: string | null
}

const formatNumber = (num: number) => new Intl.NumberFormat('es-EC').format(num)
const formatPct = (num: number) => `${num.toFixed(2)}%`

export function ConversionFunnel({
  base,
  entrega,
  aperturas,
  clics,
  tasa_entrega,
  tasa_aperturas,
  tasa_clics,
  customSteps,
  calificacion,
}: ConversionFunnelProps) {
  const palette = SCORE_PALETTES[calificacion?.toUpperCase() || ''] || DEFAULT_PALETTE

  const steps: FunnelStep[] = customSteps || [
    { label: 'Base dirigida', value: base, percentage: 100, color: palette[0] },
    { label: 'Entregados', value: entrega, percentage: tasa_entrega, color: palette[1] },
    { label: 'Aperturas', value: aperturas, percentage: tasa_aperturas, color: palette[2] },
    { label: 'Clics únicos', value: clics, percentage: tasa_clics, color: palette[3] },
  ]

  const firstValue = steps[0]?.value || 0
  const lastValue = steps[steps.length - 1]?.value || 0
  const tasaConversion = firstValue > 0 ? (lastValue / firstValue) * 100 : 0

  // SVG dimensions
  const svgW = 500
  const funnelTop = 30
  const sectionH = 100
  const gap = 12
  const centerX = svgW / 2
  const svgH = funnelTop + steps.length * (sectionH + gap) + 60

  // Half-widths at each level - creates the funnel shape
  const halfWidths = [200, 155, 110, 70]

  function buildTrapezoidPath(
    topLeft: number, topRight: number,
    bottomLeft: number, bottomRight: number,
    y: number, h: number, radius: number = 6,
  ) {
    const tl = centerX - topLeft
    const tr = centerX + topRight
    const bl = centerX - bottomLeft
    const br = centerX + bottomRight
    const top = y
    const bot = y + h

    return `
      M ${tl + radius} ${top}
      L ${tr - radius} ${top}
      Q ${tr} ${top} ${tr} ${top + radius}
      L ${br} ${bot - radius}
      Q ${br} ${bot} ${br - radius} ${bot}
      L ${bl + radius} ${bot}
      Q ${bl} ${bot} ${bl} ${bot - radius}
      L ${tl} ${top + radius}
      Q ${tl} ${top} ${tl + radius} ${top}
      Z
    `
  }

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="py-3 bg-muted/30">
        <CardTitle className="text-base font-bold text-foreground flex items-center justify-between">
          <span>Embudo de Conversión</span>
          <span className="text-xs text-muted-foreground font-normal">
            Conversión: {formatPct(tasaConversion)}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3 flex-1 flex items-center justify-center">
        <svg
          viewBox={`0 0 ${svgW} ${svgH}`}
          className="w-full"
          style={{ maxWidth: '500px' }}
        >
          <defs />

          {steps.map((step, i) => {
            const y = funnelTop + i * (sectionH + gap)
            const topHalf = halfWidths[i]
            const botHalf = halfWidths[i + 1] ?? halfWidths[i] * 0.65

            const path = buildTrapezoidPath(topHalf, topHalf, botHalf, botHalf, y, sectionH)


            return (
              <g key={step.label}>
                {/* Trapezoid shape */}
                <path
                  d={path}
                  fill={step.color}
                  className="transition-all duration-500"
                />

                {/* Center text: label + value */}
                <text
                  x={centerX}
                  y={y + sectionH / 2 - 14}
                  textAnchor="middle"
                  fill="white"
                  fontSize="12"
                  fontWeight="600"
                  className="select-none"
                >
                  {step.label}
                </text>
                <text
                  x={centerX}
                  y={y + sectionH / 2 + 8}
                  textAnchor="middle"
                  fill="white"
                  fontSize="20"
                  fontWeight="700"
                  className="select-none"
                >
                  {formatNumber(step.value)}
                </text>
                <text
                  x={centerX}
                  y={y + sectionH / 2 + 28}
                  textAnchor="middle"
                  fill="white"
                  fontSize="11"
                  opacity="0.85"
                  className="select-none"
                >
                  ({formatPct(step.percentage)})
                </text>


                {/* Connecting arrow between sections */}
                {i < steps.length - 1 && (
                  <g>
                    <line
                      x1={centerX}
                      y1={y + sectionH + 1}
                      x2={centerX}
                      y2={y + sectionH + gap - 1}
                      stroke="currentColor"
                      strokeWidth="1.5"
                      className="text-gray-300 dark:text-gray-600"
                    />
                    <polygon
                      points={`${centerX - 4},${y + sectionH + gap - 5} ${centerX + 4},${y + sectionH + gap - 5} ${centerX},${y + sectionH + gap - 1}`}
                      fill="currentColor"
                      className="text-gray-300 dark:text-gray-600"
                    />
                  </g>
                )}
              </g>
            )
          })}

          {/* Bottom conversion summary */}
          <g>
            <rect
              x={centerX - 120}
              y={funnelTop + steps.length * (sectionH + gap) + 6}
              width="240"
              height="28"
              rx="14"
              fill="currentColor"
              className="text-gray-100 dark:text-gray-800"
            />
            <text
              x={centerX}
              y={funnelTop + steps.length * (sectionH + gap) + 25}
              textAnchor="middle"
              fill="currentColor"
              fontSize="11"
              fontWeight="600"
              className="text-muted-foreground"
            >
              {formatNumber(lastValue)} de {formatNumber(firstValue)} → {formatPct(tasaConversion)} conversión
            </text>
          </g>
        </svg>
      </CardContent>
    </Card>
  )
}
